import type { ClientOfferReviewUnit } from './client-offer-review-types';
import type { unitPriceModel } from './client-offer-review-unit-card-model';
import type { CardBadge } from './client-offer-review-unit-card';

interface UnitCardHeaderRowProps {
  unit: ClientOfferReviewUnit;
  badge: CardBadge;
  photoMode: 'large' | 'small' | 'none';
  price: ReturnType<typeof unitPriceModel>;
  grayFilter: boolean;
}

/**
 * Header row: identity (address + city + voivodeship) + price only. No
 * audience figure here — client crossed out both the weekly-in-header and
 * monthly+GRP-in-panel audience displays entirely, so audience data isn't
 * shown on the card at all now (still available in the detail modal).
 * Locate/accept/reject/"Zobacz szczegóły" all live together in one action
 * row at the bottom of the always-visible detail panel instead (see
 * UnitCardExpandedPanel) — per client feedback, consolidated into a single
 * row rather than split between header and panel.
 */
export function UnitCardHeaderRow({
  unit,
  badge,
  photoMode,
  price,
  grayFilter,
}: UnitCardHeaderRowProps) {
  return (
    <div className="flex flex-wrap items-start justify-between gap-x-2.5 gap-y-1.5">
      <div className={`min-w-36 flex-1 ${grayFilter ? 'grayscale opacity-50' : ''}`}>
        {/*
         * No truncation here on purpose — the address/city is the card's primary
         * identifier and must always read in full (mobile-list convention: wrap
         * to a 2nd line rather than ellipsis-truncate the title).
         */}
        {/* voivodeship is absent on snapshots published before it was
         * snapshotted (see design.md "Voivodeship — one column, three hops")
         * — filter(Boolean) so a null value never leaves a trailing ", "
         * separator with nothing after it. */}
        <span className={`block text-sm font-semibold ${unit.sold ? 'line-through' : ''}`}>
          {[unit.address, unit.city, unit.voivodeship].filter(Boolean).join(', ')}
        </span>
        {photoMode !== 'large' && badge ? (
          <span
            className={`mt-0.5 inline-block max-w-full overflow-hidden text-ellipsis whitespace-nowrap rounded-full px-2 text-2xs font-semibold ${badge.className}`}
          >
            {badge.label}
          </span>
        ) : null}
        <div className="overflow-hidden text-ellipsis whitespace-nowrap text-2xs text-muted-foreground">
          {unit.format}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div
          className={`text-sm font-semibold tabular-nums ${price.className} ${price.strike ? 'line-through' : ''}`}
        >
          {price.text}
        </div>
        <div className="text-2xs uppercase tracking-wide text-muted-foreground">{price.sub}</div>
      </div>
    </div>
  );
}
